
import { useEffect, useState } from 'react';
import './App.css';

import { createTheme, ThemeProvider } from '@mui/material/styles';
import {
  HashRouter,
  Routes,
  Route,
  Navigate
} from 'react-router-dom';

import { useMsal } from '@azure/msal-react';
import { InteractionStatus } from '@azure/msal-browser';

import EngagementForm from './components/EngagementForm.js';
import InternalUserLogin from './components/InternalUserLogin.js';

// =========================================================
// THEME
// =========================================================

const theme = createTheme({
  palette: {
    primary: {
      main: '#1976d2',
    },
  },

  components: {
    MuiFormControlLabel: {
      styleOverrides: {
        root: {},
        label: {
          fontSize: '0.875rem',
          fontWeight: 'normal',
          color: 'primary.main',
        },
      },
    },

    MuiFilledInput: {
      styleOverrides: {
        root: {
          backgroundColor: '#f5f9fd',
          '&:hover': {
            backgroundColor: '#dceaf8',
          },
        },
      },
    },

    MuiNativeSelect: {
      styleOverrides: {
        select: {
          backgroundColor: '#f0f0f0',
          padding: '10px',
          '&:focus': {
            backgroundColor: '#e0e0e0',
          },
        },
      },
    },

    MuiFormLabel: {
      styleOverrides: {
        root: {
          color: 'black',
          '&.Mui-focused': {
            color: 'primary.main',
          },
        },
      },
    },
  },
});

// =========================================================
// AUTHENTICATION LOADING SCREEN
// =========================================================

function AuthenticationLoading() {
  return (
    <div
      className="authentication-loading"
      role="status"
      aria-live="polite"
    >
      <div className="authentication-spinner" />

      <div className="authentication-title">
        Authenticating...
      </div>

      <div className="authentication-message">
        Please wait while we verify your access.
      </div>
    </div>
  );
}

// =========================================================
// PUBLIC APPLICATION
// =========================================================

function PublicApp() {
  return (
    <ThemeProvider theme={theme}>
      <HashRouter>
        <div className="App">
          <header className="App-header">
            <Routes>
              <Route
                path="/Engagement"
                element={<EngagementForm />}
              />

              <Route
                path="*"
                element={
                  <Navigate
                    to="/Engagement"
                    replace
                  />
                }
              />
            </Routes>
          </header>
        </div>
      </HashRouter>
    </ThemeProvider>
  );
}

// =========================================================
// INTERNAL APPLICATION
// =========================================================

function InternalApp() {
  const { instance, accounts } = useMsal();

  const account =
    instance.getActiveAccount() || accounts[0];

  const internalUser = account
    ? {
        name: account.name || '',
        email: account.username || '',
      }
    : null;

  return (
    <ThemeProvider theme={theme}>
      <HashRouter>
        <div className="App">
          <header className="App-header">
            <Routes>
              <Route
                path="/Engagement"
                element={
                  <EngagementForm
                    internalUser={internalUser}
                  />
                }
              />

              <Route
                path="*"
                element={
                  <Navigate
                    to="/Engagement"
                    replace
                  />
                }
              />
            </Routes>
          </header>
        </div>
      </HashRouter>
    </ThemeProvider>
  );
}

// =========================================================
// MICROSOFT GRAPH CONFIGURATION
// =========================================================

const GRAPH_BASE =
  'https://graph.microsoft.com/v1.0';

const GRAPH_SCOPES = [
  'https://graph.microsoft.com/Files.Read.All',
  'https://graph.microsoft.com/Sites.Read.All',
];

// Engagement SharePoint document library.
const DRIVE_ID =
  window.location.hostname === 'portal.hgeo.doe.gov'
    ? 'b!JXNI1m1B2Ue8UVj6dA3hYIpykSaQWvZFqqr6D3nxXXCdPBHBtWdcTZZEwGw3Idaz'
    : 'b!E1cZrgIrFUyc3P10Gx2n7RdRbt9UlTtHtB03KFREGGtrkUp2LYKESbziiuSUVNeh';

// File is located in:
// Engagement / Shared Documents / AccessibleData
const GRAPH_FILE_URL =
  `${GRAPH_BASE}/drives/${DRIVE_ID}/root:/AccessibleData/system_ea_et.json`;

const TRACKER_URL =
  window.location.hostname === 'portal.hgeo.doe.gov'
    ? 'https://usdoe.sharepoint.com/sites/RM-FECM-IRM/Engagement/Lists/Engagement%20Request%20Tracker/NewForm.aspx'
    : 'https://usdoe.sharepoint.com/sites/sandbox-hgeo/Engagement/Lists/Engagement%20Request%20Tracker/NewForm.aspx';

// =========================================================
// INTERNAL USER ROUTER
// =========================================================

function InternalUserRouter() {
  const { instance, accounts, inProgress } = useMsal();

  const account =
    instance.getActiveAccount() || accounts[0];

  const email =
    account?.username?.trim().toLowerCase();

  const [status, setStatus] = useState('loading');
  const [error, setError] = useState('');
  const [retry, setRetry] = useState(0);

  useEffect(() => {
    if (
      !account ||
      !email ||
      inProgress !== InteractionStatus.None
    ) {
      return;
    }

    const controller = new AbortController();


async function checkUser() {
  setStatus('loading');
  setError('');

  try {
    console.log('[SystemUsers] Starting verification');
    console.log('[SystemUsers] Hostname:', window.location.hostname);
    console.log('[SystemUsers] Configured Drive ID:', DRIVE_ID);

    // STEP 1: Acquire Microsoft Graph token
    const tokenResponse = await instance.acquireTokenSilent({
      scopes: GRAPH_SCOPES,
      account,
    });

    if (controller.signal.aborted) return;

    console.log('[SystemUsers] Graph token acquired');

    const graphHeaders = {
      Authorization: `Bearer ${tokenResponse.accessToken}`,
    };

    // STEP 2: Identify production or sandbox site
    const isProduction =
      window.location.hostname.toLowerCase() ===
      'portal.hgeo.doe.gov';

    const sitePath = isProduction
      ? '/sites/RM-FECM-IRM/Engagement'
      : '/sites/sandbox-hgeo/Engagement';

    const siteUrl =
      `${GRAPH_BASE}/sites/usdoe.sharepoint.com:${sitePath}`;

    console.log('[SystemUsers] Checking site:', sitePath);

    const siteResponse = await fetch(siteUrl, {
      headers: graphHeaders,
      signal: controller.signal,
    });

    if (!siteResponse.ok) {
      throw new Error(
        `Site lookup failed: HTTP ${siteResponse.status}`
      );
    }

    const siteData = await siteResponse.json();

    console.log('[SystemUsers] Site URL:', siteData.webUrl);
    console.log('[SystemUsers] Site ID:', siteData.id);

    // STEP 3: List document libraries
    const drivesResponse = await fetch(
      `${GRAPH_BASE}/sites/${siteData.id}/drives`,
      {
        headers: graphHeaders,
        signal: controller.signal,
      }
    );

    if (!drivesResponse.ok) {
      throw new Error(
        `Library lookup failed: HTTP ${drivesResponse.status}`
      );
    }

    const drivesData = await drivesResponse.json();

    console.log('[SystemUsers] Document libraries:');

    console.table(
      drivesData.value.map(drive => ({
        name: drive.name,
        id: drive.id,
        webUrl: drive.webUrl,
        matchesConfiguredDrive: drive.id === DRIVE_ID,
      }))
    );

    // STEP 4: Find the actual Documents library
    const expectedLibraryUrl =
      `https://usdoe.sharepoint.com${sitePath}/Shared Documents`;

    const normalizeUrl = url =>
      decodeURIComponent(url || '')
        .replace(/\/$/, '')
        .toLowerCase();

    const documentsDrive = drivesData.value.find(
      drive =>
        normalizeUrl(drive.webUrl) ===
        normalizeUrl(expectedLibraryUrl)
    );

    if (!documentsDrive) {
      throw new Error(
        'Documents library not found in SharePoint site.'
      );
    }

    console.log(
      '[SystemUsers] Actual Documents Drive ID:',
      documentsDrive.id
    );

    if (documentsDrive.id !== DRIVE_ID) {
      console.warn(
        '[SystemUsers] Configured Drive ID differs from actual Documents Drive ID.'
      );
    }

    // STEP 5: Check AccessibleData folder
    const folderUrl =
      `${GRAPH_BASE}/drives/${documentsDrive.id}` +
      `/root:/AccessibleData:/children`;

    const folderResponse = await fetch(folderUrl, {
      headers: graphHeaders,
      signal: controller.signal,
    });

    console.log(
      '[SystemUsers] AccessibleData folder status:',
      folderResponse.status
    );

    if (!folderResponse.ok) {
      throw new Error(
        `AccessibleData folder lookup failed: HTTP ${folderResponse.status}`
      );
    }

    const folderData = await folderResponse.json();

    console.log(
      '[SystemUsers] Files in AccessibleData:',
      folderData.value.map(file => file.name)
    );

    // STEP 6: Retrieve JSON metadata using actual Drive ID
    const fileUrl =
      `${GRAPH_BASE}/drives/${documentsDrive.id}` +
      `/root:/AccessibleData/system_ea_et.json`;

    console.log('[SystemUsers] Checking JSON file...');

    const fileResponse = await fetch(fileUrl, {
      headers: graphHeaders,
      cache: 'no-store',
      signal: controller.signal,
    });

    console.log(
      '[SystemUsers] JSON metadata status:',
      fileResponse.status
    );

    if (!fileResponse.ok) {
      const errorData = await fileResponse.json().catch(() => ({}));

      throw new Error(
        errorData.error?.message ||
        `Graph file lookup failed: HTTP ${fileResponse.status}`
      );
    }

    const fileMetadata = await fileResponse.json();

    console.log(
      '[SystemUsers] JSON file found:',
      fileMetadata.name
    );

    // STEP 7: Download JSON
    const downloadUrl =
      fileMetadata['@microsoft.graph.downloadUrl'];

    if (!downloadUrl) {
      throw new Error(
        'Microsoft Graph did not return a download URL.'
      );
    }

    const jsonResponse = await fetch(downloadUrl, {
      cache: 'no-store',
      signal: controller.signal,
    });

    if (!jsonResponse.ok) {
      throw new Error(
        `JSON download failed: HTTP ${jsonResponse.status}`
      );
    }

    const users = await jsonResponse.json();

    if (!Array.isArray(users)) {
      throw new Error(
        'Invalid system_ea_et.json format.'
      );
    }

    if (controller.signal.aborted) return;

    console.log(
      '[SystemUsers] JSON loaded:',
      users.length,
      'users'
    );

    // STEP 8: Compare logged-in email
    const found = users.some(
      user =>
        typeof user.Email === 'string' &&
        user.Email.trim().toLowerCase() === email
    );

    console.log(
      '[SystemUsers] Logged-in user found:',
      found
    );

    // STEP 9: Redirect or display InternalApp
    if (found) {
      setStatus('redirecting');
      window.location.replace(TRACKER_URL);
    } else {
      setStatus('internal');
    }

  } catch (err) {
    if (controller.signal.aborted) return;

    console.error('[SystemUsers] Error:', err);

    setError(
      err.message || 'Unable to verify user.'
    );

    setStatus('error');
  }
}    

    checkUser();

    return () => {
      controller.abort();
    };

  }, [instance, account, email, inProgress, retry]);

  // -------------------------------------------------------
  // Show authentication screen while:
  // - MSAL is initializing
  // - Graph token is being acquired
  // - JSON file is being downloaded
  // - Browser is redirecting
  // -------------------------------------------------------

  if (
    !account ||
    status === 'loading' ||
    status === 'redirecting'
  ) {
    return <AuthenticationLoading />;
  }

  // -------------------------------------------------------
  // Error with Retry
  // -------------------------------------------------------

  if (status === 'error') {
    return (
      <div style={{ padding: 20 }}>
        <p>Unable to check users: {error}</p>

        <button
          onClick={() => setRetry(n => n + 1)}
        >
          Retry
        </button>
      </div>
    );
  }

  // -------------------------------------------------------
  // User is not listed in JSON
  // -------------------------------------------------------

  return <InternalApp />;
}

// =========================================================
// MAIN APPLICATION
// =========================================================

function App({ isInternalSite }) {

  // Public application remains unchanged.
  if (!isInternalSite) {
    return <PublicApp />;
  }

  // Internal:
  // Authenticate -> Check JSON -> Redirect or InternalApp
  return (
    <InternalUserLogin>
      <InternalUserRouter />
    </InternalUserLogin>
  );
}

export default App;